/**
 * Scope matching helper — client-side mirror of the server's Scope.HasScope logic.
 *
 * Matching rules (AND semantics for arrays):
 *  - Wildcard "*" in granted satisfies any required scope.
 *  - Prefix wildcard "case:*" in granted satisfies any "case:<x>" required scope.
 *  - Exact match satisfies the required scope.
 *  - For an array of required scopes, ALL must be satisfied (AND semantics).
 *
 * Used by:
 *  - ScopeGate component (page-level enforcement)
 *  - Sidebar item locking (UX hint)
 *  - Operator store hasScope selector
 *
 * Important: this is a UX primitive, not a security boundary. The gateway's
 * RequireScope middleware is the authoritative enforcement point.
 *
 * Task 3552c.
 */

/**
 * Returns true if the granted scope set satisfies all required scope(s).
 *
 * @param granted - The caller's effective scope list (e.g. ["*"] or ["engineering:read", "case:read"])
 * @param required - A single scope string or an array of scopes (AND semantics for arrays)
 */
export function hasScope(
  granted: readonly string[],
  required: string | readonly string[],
): boolean {
  // Full wildcard: satisfies everything.
  if (granted.includes('*')) return true

  const reqs = Array.isArray(required) ? required : [required]
  return reqs.every((r) => grantedSatisfies(granted, r))
}

/**
 * Returns true if any entry in `granted` satisfies the single `required` scope.
 */
function grantedSatisfies(granted: readonly string[], required: string): boolean {
  // Exact match.
  if (granted.includes(required)) return true

  // Prefix wildcard: "case:*" satisfies "case:read", "case:write", etc.
  return granted.some((g) => {
    if (!g.endsWith(':*')) return false
    const prefix = g.slice(0, -1) // e.g. "case:"
    return required.startsWith(prefix)
  })
}
